import { useEffect, useState } from 'react'
import { Facebook, MessageCircle, Moon, Music, Pause, Sun, MapPin, Clock, Send } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { slideInBottom, staggerContainer } from '../lib/animations'

export default function Footer() {
  const navigate = useNavigate()
  const [isDark, setIsDark] = useState(false)
  const [isMusicOn, setIsMusicOn] = useState(false)

  useEffect(() => {
    const stored = localStorage.getItem('digigo-theme')
    const shouldBeDark = stored
      ? stored === 'dark'
      : window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    setIsDark(shouldBeDark)

    const onThemeChanged = (ev: Event) => {
      const nextIsDark = (ev as CustomEvent<{ isDark: boolean }>).detail?.isDark
      if (typeof nextIsDark === 'boolean') setIsDark(nextIsDark)
    }
    window.addEventListener('digigo-theme-changed', onThemeChanged)

    const onMusicChanged = (ev: Event) => {
      const next = (ev as CustomEvent<{ isMusicOn: boolean }>).detail?.isMusicOn
      if (typeof next === 'boolean') setIsMusicOn(next)
    }
    window.addEventListener('digigo-music-changed', onMusicChanged)

    return () => {
      window.removeEventListener('digigo-theme-changed', onThemeChanged)
      window.removeEventListener('digigo-music-changed', onMusicChanged)
    }
  }, [])

  const toggleTheme = () => {
    setIsDark(prev => {
      const next = !prev
      localStorage.setItem('digigo-theme', next ? 'dark' : 'light')
      document.documentElement.classList.toggle('dark', next)
      window.dispatchEvent(new CustomEvent('digigo-theme-changed', { detail: { isDark: next } }))
      return next
    })
  }

  const toggleMusic = () => {
    window.dispatchEvent(new Event('digigo-music-toggle'))
  }

  const openProductsCategory = (category: string) => {
    const params = new URLSearchParams()
    if (category !== 'all') params.set('category', category)
    const search = params.toString()
    navigate({ pathname: '/products', search: search ? `?${search}` : '' })
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }

  const openPolicySection = (sectionId: string) => {
    navigate({ pathname: '/policy', hash: `#${sectionId}` })
  }

  return (
    <footer className="bg-slate-950 text-slate-200">
      <div className="mx-auto max-w-6xl px-4 py-10">
        <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" variants={staggerContainer(0.1)} initial="initial" whileInView="animate" viewport={{ once: true, margin: '-40px' }}>
          <motion.div {...slideInBottom(0)}>
            <div className="text-sm font-bold tracking-wide text-white mb-4">THÔNG TIN LIÊN HỆ</div>
            <div className="text-sm text-slate-300 mb-4">
              Tạp hóa nội dung số - Nền tảng cung cấp sản phẩm/dịch vụ số, giao nhanh, hỗ trợ qua Chat Support và nhóm Telegram.
            </div>
            <div className="flex items-center gap-3 mb-4">
              <motion.a
                whileHover={{ scale: 1.15, rotate: 5 }}
                href="https://t.me/+_KxbPXTAu7AzYjg1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center p-2 rounded-full bg-white/10 hover:bg-white/15 transition-colors"
                title="Nhóm Telegram"
              >
                <Send className="w-5 h-5" />
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.15, rotate: 5 }}
                href="https://zalo.me/0794174833"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center p-2 rounded-full bg-white/10 hover:bg-white/15 transition-colors"
                title="Zalo"
              >
                <MessageCircle className="w-5 h-5" />
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.15, rotate: 5 }}
                href="https://www.facebook.com/profile.php?id=61585243452584"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center p-2 rounded-full bg-white/10 hover:bg-white/15 transition-colors"
                title="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </motion.a>
            </div>
            <div className="space-y-3 text-sm text-slate-300">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 text-yellow-300" />
                <div>
                  Không công khai (hỗ trợ trực tuyến)
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 mt-0.5 text-yellow-300" />
                <div>Thời gian làm việc: 8:00 - 21:00</div>
              </div>
              <div className="pt-1 text-xs text-slate-400">
                Mã số thuế cá nhân: <span className="text-slate-200 font-medium">079203027622</span>
              </div>
            </div>
          </motion.div>

          <motion.div {...slideInBottom(0.08)}>
            <div className="text-sm font-bold tracking-wide text-white mb-4">DANH MỤC</div>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>
                <button type="button" onClick={() => openProductsCategory('all')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Tất cả
                </button>
              </li>
              <li>
                <button type="button" onClick={() => openProductsCategory('software')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Phần mềm
                </button>
              </li>
              <li>
                <button type="button" onClick={() => openProductsCategory('game')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Game
                </button>
              </li>
              <li>
                <button type="button" onClick={() => openProductsCategory('education')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Giáo dục
                </button>
              </li>
              <li>
                <button type="button" onClick={() => openProductsCategory('programming')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Lập trình
                </button>
              </li>
              <li>
                <button type="button" onClick={() => openProductsCategory('social')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Mạng xã hội
                </button>
              </li>
              <li>
                <button type="button" onClick={() => openProductsCategory('creation')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Sáng tạo
                </button>
              </li>
              <li>
                <button type="button" onClick={() => openProductsCategory('vehicle')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Phương tiện
                </button>
              </li>
            </ul>
          </motion.div>

          <motion.div {...slideInBottom(0.16)}>
            <div className="text-sm font-bold tracking-wide text-white mb-4">CHÍNH SÁCH</div>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>
                <button type="button" onClick={() => openPolicySection('thanh-toan')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Chính sách thanh toán
                </button>
              </li>
              <li>
                <button type="button" onClick={() => openPolicySection('dieu-kien-giao-dich')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Điều kiện giao dịch chung
                </button>
              </li>
              <li>
                <button type="button" onClick={() => openPolicySection('hang-hoa')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Thông tin hàng hóa, dịch vụ
                </button>
              </li>
              <li>
                <button type="button" onClick={() => openPolicySection('bao-mat')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Chính sách bảo mật
                </button>
              </li>
              <li>
                <button type="button" onClick={() => openPolicySection('khieu-nai')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Khiếu nại, tranh chấp
                </button>
              </li>
            </ul>
          </motion.div>

          <motion.div {...slideInBottom(0.24)}>
            <div className="text-sm font-bold tracking-wide text-white mb-4">LIÊN KẾT NHANH</div>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>
                <button type="button" onClick={() => navigate('/products')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Trang chủ
                </button>
              </li>
              <li>
                <button type="button" onClick={() => navigate('/topup')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Nạp tiền
                </button>
              </li>
              <li>
                <button type="button" onClick={() => navigate('/utilities')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Tiện ích
                </button>
              </li>
              <li>
                <button type="button" onClick={() => navigate('/profile')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Tài khoản
                </button>
              </li>
              <li>
                <button type="button" onClick={() => openPolicySection('dieu-kien-giao-dich')} className="hover:text-white">
                  <span className="text-yellow-300 mr-2">›</span>Chính sách
                </button>
              </li>
            </ul>

            <div className="rounded-lg border border-white/10 bg-white/5 p-3 mt-4">
              <div className="text-sm font-semibold text-white">Tùy chỉnh</div>
              <div className="flex items-center gap-2 mt-3">
                <button
                  type="button"
                  onClick={toggleMusic}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-yellow-400/10 text-yellow-200 hover:bg-yellow-400/15 transition-colors"
                  title={isMusicOn ? 'Tắt nhạc nền' : 'Bật nhạc nền'}
                  aria-label={isMusicOn ? 'Tắt nhạc nền' : 'Bật nhạc nền'}
                >
                  {isMusicOn ? <Pause className="w-5 h-5" /> : <Music className="w-5 h-5" />}
                  <span className="text-sm font-medium">Âm nhạc</span>
                </button>

                <button
                  type="button"
                  onClick={toggleTheme}
                  className="inline-flex items-center justify-center p-2 rounded-full bg-white/10 text-slate-100 hover:bg-white/15 transition-colors"
                  title={isDark ? 'Chuyển sang chế độ sáng' : 'Chuyển sang chế độ tối'}
                  aria-label={isDark ? 'Chuyển sang chế độ sáng' : 'Chuyển sang chế độ tối'}
                >
                  {isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <div className="border-t border-white/10 bg-black/30">
        <div className="mx-auto max-w-6xl px-4 py-4 text-xs text-slate-300 flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="sm:mr-6">© Tạp hóa nội dung số. All rights reserved.</div>
          <div className="flex items-center gap-4 flex-wrap sm:flex-1 sm:justify-center">
            <button type="button" onClick={() => openPolicySection('dieu-kien-giao-dich')} className="text-slate-300 hover:text-white">
              Điều khoản sử dụng
            </button>
            <button type="button" onClick={() => openPolicySection('bao-mat')} className="text-slate-300 hover:text-white">
              Chính sách bảo mật
            </button>
            <button type="button" onClick={() => openPolicySection('dieu-kien-giao-dich')} className="text-slate-300 hover:text-white">
              Chính sách đổi trả
            </button>
          </div>
          <div className="sm:ml-6 sm:text-right text-sm font-bold tracking-wide text-yellow-200">2026</div>
        </div>
      </div>
    </footer>
  )
}
